import React, {
    useDeferredValue,
    useMemo,
    useState
} from 'react';
import PropTypes from 'prop-types';

import {
    Menu
} from '@headlessui/react';

import {
    useIsExporting
} from '../hooks/useExport.js';

import RIQButton from './RIQButton.jsx';
import {
    RIQInfoPopover,
    useRIQTooltip
} from './RIQPopover.jsx';
import useTrackingId from '../hooks/useTrackingId.js';

const ALL = '__ALL__';

export function DropdownItem({disabled, dropdownOption, hidden, onChange, selected, tooltipText}) {

    function onClick(e) {
        // This prevents the dropdown from closing on selection.
        e.preventDefault();
        onChange(dropdownOption);
    }

    const item = <>
        <input
            type='checkbox'
            className='tlw-pointer-events-none'
            checked={Boolean(selected)}
            readOnly={true}
            disabled={disabled}
        />
        <span
            className='tlw-ml-4'>
            {dropdownOption.name}
        </span>
        {dropdownOption.context &&
            <span className={disabled ? 'tlw-text-gray-500' : 'tlw-text-gray-700'}>
                {dropdownOption.context}
            </span>
        }
    </>;

    let itemClassName = 'tlw-group/item tlw-table-row';
    if (hidden) {
        itemClassName += ' tlw-collapse';
    }

    const {setTriggerRef, tooltipNode} = useRIQTooltip({
        text: tooltipText,
        placement: 'bottom',
        disabled: !disabled
    });

    return (
        <>
            {
                <Menu.Item key={dropdownOption.id}>
                    {({active}) => (
                        <div className={itemClassName}>
                            <div className='tlw-flex tlw-flex-row'>
                                <div
                                    className='tlw-grow'
                                    ref={setTriggerRef}>
                                    <button
                                        className={`tlw-mb-1 tlw-block tlw-w-full tlw-flex-grow tlw-cursor-pointer tlw-border-none tlw-bg-transparent tlw-py-0 tlw-text-left tlw-text-md
                                            ${dropdownOption.info ? ' tlw-pl-7.5 tlw-pr-0' : ' tlw-px-7.5'}
                                            ${disabled ? ' tlw-text-gray-500' : ' tlw-text-gray-900'}`
                                        }
                                        onClick={onClick}
                                        disabled={disabled}
                                    >
                                        {item}
                                    </button>
                                </div>

                                {dropdownOption.info &&
                                    <span
                                        className='tlw-mr-7.5 tlw-flex tlw-items-end tlw-justify-between'
                                    >
                                        <span
                                            className='tlw-opacity-0 tlw-transition-opacity tlw-delay-300 tlw-duration-150 tlw-ease-in group-hover/item_tlw-opacity-100'
                                        >
                                            <RIQInfoPopover
                                                description={dropdownOption.info}
                                            />
                                        </span>

                                    </span>
                                }
                            </div>
                        </div>

                    )}
                </Menu.Item>
            }
            {tooltipNode}
        </>
    );
}

function ItemFilter({icon = 'search', onChange, onIconClick, placeholder = 'Search', term}) {

    const hasValue = Boolean(term);
    const activeIcon = (hasValue ? 'times' : icon);
    let iconClassName = `tlw-absolute tlw-top-[2.5rem] tlw-right-[2.5rem] tlw-text-gray-650 fa fa-${activeIcon} fa-fw`;
    if (hasValue) {
        iconClassName += ' tlw-cursor-pointer';
    }

    function handleIconClick(e) {
        // NOTE we only want clicking the icon supported when a value exists
        if (hasValue && onIconClick) {
            onIconClick(e);
        }
    }

    return <div className='tlw-relative tlw-p-7.5 tlw-pb-5'>
        <input
            className='tlw-w-full tlw-text-ellipsis tlw-rounded-sm tlw-border tlw-border-solid tlw-border-gray-600 tlw-py-3 tlw-pl-6 tlw-pr-12 tlw-text-md tlw-leading-20 tlw-shadow-sm focus_tlw-border-blue-400 focus_tlw-shadow-inner focus_tlw-outline-none disabled_tlw-pointer-events-none disabled_tlw-cursor-not-allowed disabled_tlw-opacity-65 disabled_tlw-shadow-none'
            onChange={onChange}
            placeholder={placeholder}
            value={term}/>
        <i
            // NOTE satisfies eslint rules for accessbility
            aria-hidden='true'
            className={iconClassName}
            onClick={handleIconClick}/>
    </div>;
}

function DropdownMenu({title, dropdownOptions, dropdownItemsClassName, showAllOption, itemFilterEnabled, itemFilterFcn, itemFilterHint, itemFilterIcon, onItemSelectionsCommited, otherItems, noteText, tooltipText, trackingId}) {

    const originallySelectedIds = _(dropdownOptions)
        .filter('selected')
        .map('id')
        .keyBy()
        .value();

    const [itemFilterTerm, setItemFilterTerm] = useState('');
    const deferredItemFilterTerm = useDeferredValue(itemFilterTerm);

    const visibleDropdownOptions = useMemo(() => {
        const init = {};
        if (showAllOption) {
            init[ALL] = true;
        }

        return _.reduce(dropdownOptions, (memo, dropdownOption) => {
            return _.set(memo, dropdownOption.id, (
                (!itemFilterEnabled && _.isEmpty(deferredItemFilterTerm)) ||
                (itemFilterFcn && itemFilterFcn(deferredItemFilterTerm, dropdownOption)) ||
                (dropdownOption.name.toLocaleLowerCase().includes(deferredItemFilterTerm.toLocaleLowerCase()))
            ));
        }, init);
    }, [showAllOption, deferredItemFilterTerm]); // eslint-disable-line react-hooks/exhaustive-deps

    const [currentlySelectedIds, setCurrentlySelectedIds] = useState(originallySelectedIds);

    const resetState = function () {
        setCurrentlySelectedIds(originallySelectedIds);
        setItemFilterTerm('');
    };

    const isSelectionStateChanged = !_.isEqual(currentlySelectedIds, originallySelectedIds);
    const isSelectionStateValid = _.size(currentlySelectedIds) > 0;

    const allSelectableIds = _(dropdownOptions)
        .reject('disabled')
        .map('id')
        .value();

    function onSearchTermChange(e) {
        setItemFilterTerm(e.target.value);
    }

    function onSearchIconClick(e) {
        if (itemFilterTerm) {
            setItemFilterTerm('');
        }
    }

    const options = [].concat(dropdownOptions);
    if (showAllOption) {
        options.splice(0, 0, {
            id: ALL,
            name: 'All'
        });
    }

    const allOptionsCurrentlySelected = _.size(allSelectableIds) === _.size(_.omit(currentlySelectedIds, ALL));
    const activeFilter = _.size(allSelectableIds) !== _.size(_.omit(originallySelectedIds, ALL));

    function onItemToggled(dropdownOption) {
        const id = dropdownOption.id;
        let newSelectedIds;

        if (id === ALL) {
            if (allOptionsCurrentlySelected) {
                newSelectedIds = {};
            } else {
                newSelectedIds = _.keyBy(allSelectableIds);
            }
        } else {
            if (currentlySelectedIds[id]) {
                newSelectedIds = _.omit(currentlySelectedIds, id);
            } else {
                newSelectedIds = _.defaults({
                    [id]: id
                }, currentlySelectedIds);
            }
        }

        if (!_.isEqual(newSelectedIds, currentlySelectedIds)) {
            setCurrentlySelectedIds(newSelectedIds);
        }
    }

    function onCancelClicked(closeFcn) {
        resetState();
        closeFcn();
    }

    function onApplyClicked(closeFcn) {
        onItemSelectionsCommited(_.keys(currentlySelectedIds));

        resetState();
        closeFcn();
    }

    const isDisabled = _.size(allSelectableIds) === 1;

    const {setTriggerRef, tooltipNode} = useRIQTooltip({
        text: tooltipText,
        placement: 'bottom',
        disabled: !isDisabled
    });

    const trackingRef = useTrackingId(trackingId);

    return (
        <>
            {
                <Menu
                    as='div'
                    className='tlw-relative tlw-inline-block tlw-text-left'
                    ref={(el) => {
                        setTriggerRef(el);
                        trackingRef.current = el;
                    }}
                >
                    {({open, close}) => (
                        <>
                            <Menu.Button
                                className={
                                    [
                                        open ? 'tlw-border-[#d4d4d4] tlw-bg-[#e6e6e6] tlw-text-gray-800' : 'tlw-border-gray-600 tlw-bg-white tlw-text-gray-800 hover_tlw-bg-gray-100',
                                        `tlw-w-full tlw-flex tlw-items-center tlw-justify-between tlw-rounded-sm tlw-border tlw-border-solid tlw-shadow-sm tlw-px-6 tlw-py-1.5 tlw-h-17`,
                                        activeFilter ? 'tlw-border-l-5 tlw-border-l-gray-800' : '',
                                        isDisabled ? 'tlw-border-gray-200 tlw-shadow-none tlw-opacity-65 tlw-border-l-5 tlw-border-l-gray-800' : ''
                                    ].join(' ')}
                                disabled={isDisabled}
                                onFocus={resetState}
                            >
                                {title}
                                <span
                                    className='fa fa-angle-down tlw-ml-1.5'
                                />
                            </Menu.Button>

                            <Menu.Items className={`tlw-absolute tlw-right-0 tlw-z-10 tlw-my-1 tlw-min-w-80 tlw-origin-top-right tlw-whitespace-nowrap tlw-rounded tlw-border tlw-border-solid tlw-border-black/[0.15] tlw-bg-white tlw-p-0 tlw-shadow-dropdown focus_tlw-outline-none ${dropdownItemsClassName ?? ''}`}>
                                {itemFilterEnabled &&
                                    <>
                                        <ItemFilter
                                            onChange={onSearchTermChange}
                                            onIconClick={onSearchIconClick}
                                            icon={itemFilterIcon}
                                            placeholder={itemFilterHint}
                                            term={itemFilterTerm} />
                                        {noteText && <>
                                            <p className='tlw-whitespace-pre-line tlw-pl-7.5 tlw-text-sm'>{noteText}</p>
                                        </>
                                        }
                                        <div className='tlw-mx-[-2px] tlw-my-1 tlw-h-0.5 tlw-bg-gray-300'/>
                                    </>
                                }

                                <div className='tlw-table tlw-max-h-[288px] tlw-overflow-scroll tlw-py-5'>
                                    {_.map(options, dropdownOption => {
                                        const isSelected = (dropdownOption.id === ALL ?
                                            _.size(allSelectableIds) === _.size(currentlySelectedIds) :
                                            Boolean(currentlySelectedIds[dropdownOption.id]));

                                        return (
                                            <DropdownItem
                                                key={dropdownOption.id}
                                                disabled={dropdownOption.disabled}
                                                dropdownOption={dropdownOption}
                                                hidden={!visibleDropdownOptions[dropdownOption.id]}
                                                onChange={onItemToggled}
                                                selected={isSelected}
                                                tooltipText={tooltipText}
                                            />
                                        );
                                    })}
                                    {
                                        otherItems
                                    }
                                </div>

                                {isSelectionStateChanged && isSelectionStateValid && <>
                                    <div className='tlw-mx-[-2px] tlw-my-1 tlw-h-0.5 tlw-bg-gray-300'/>
                                    <div className='tlw-flex tlw-gap-5 tlw-p-5'>
                                        <RIQButton
                                            className='tlw-flex-grow tlw-basis-0 tlw-px-10 tlw-py-3'
                                            variant='default'
                                            onClick={() => onCancelClicked(close)}>
                                            Cancel
                                        </RIQButton>
                                        <RIQButton
                                            className='tlw-flex-grow tlw-basis-0 tlw-px-10 tlw-py-3'
                                            variant='primary'
                                            onClick={() => onApplyClicked(close)}>
                                            Apply
                                        </RIQButton>
                                    </div>
                                </>}
                            </Menu.Items>
                        </>
                    )}
                </Menu>
            }
            {tooltipNode}
        </>
    );
}

RIQMultiSelectDropdown.propTypes = {
    title: PropTypes.node.isRequired,
    dropdownItemsClassName: PropTypes.string,
    dropdownOptions: PropTypes.array.isRequired,
    showAllOption: PropTypes.bool,
    onItemSelectionsCommited: PropTypes.func.isRequired, // called when the dropdown changes are applied
    itemFilterEnabled: PropTypes.bool,
    itemFilterFcn: PropTypes.func,
    itemFilterHint: PropTypes.string,
    itemFilterIcon: PropTypes.string,
    otherItems: PropTypes.node,
    noteText: PropTypes.string,
    tooltipText: PropTypes.string
};

export default function RIQMultiSelectDropdown({title, dropdownOptions, dropdownItemsClassName, showAllOption, itemFilterEnabled, itemFilterFcn, itemFilterHint, itemFilterIcon, onItemSelectionsCommited, otherItems, noteText, tooltipText, trackingId}) {

    const isExporting = useIsExporting();

    // using these ids as the key will make React re-render the component when the commited set of ids changes.
    const selectedIds = _(dropdownOptions)
        .filter('selected')
        .map('id')
        .value()
        .sort()
        .join('-');

    return (
        <>
            {!isExporting &&
                <DropdownMenu
                    key={selectedIds}
                    title={title}
                    dropdownOptions={dropdownOptions}
                    dropdownItemsClassName={dropdownItemsClassName}
                    showAllOption={showAllOption}
                    itemFilterEnabled={itemFilterEnabled}
                    itemFilterFcn={itemFilterFcn}
                    itemFilterHint={itemFilterHint}
                    itemFilterIcon={itemFilterIcon}
                    onItemSelectionsCommited={onItemSelectionsCommited}
                    otherItems={otherItems}
                    noteText={noteText}
                    tooltipText={tooltipText}
                    trackingId={trackingId}
                />
            }
        </>
    );
}
